// apiUsers.ts

import axios from 'axios';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// User APIs
export async function getUsers() {
  const headersList = {
    Accept: '*/*',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };

  const reqOptions = {
    url: `${backendUrl}/api/users`,
    method: 'GET' as const,
    headers: headersList,
  };

  const { data } = await axios.request(reqOptions);
  return data;
}

export async function deleteUser(id: string) {
  const headersList = {
    Accept: '*/*',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };

  const reqOptions = {
    url: `${backendUrl}/api/users/${id}`,
    method: 'DELETE' as const,
    headers: headersList,
  };

  const { data } = await axios.request(reqOptions);
  return data;
}

export async function addUser(
  first_name: string,
  last_name: string,
  email: string,
  roles: string[],
) {
  const headersList = {
    Accept: '*/*',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };

  const bodyContent = JSON.stringify({
    first_name,
    last_name,
    email,
    role: roles,
  });

  const reqOptions = {
    url: `${backendUrl}/api/users`,
    method: 'POST' as const,
    headers: headersList,
    data: bodyContent,
  };

  const { data } = await axios.request(reqOptions);
  return data;
}

export async function editUser(
  first_name: string,
  last_name: string,
  id: string,
  roles: string[],
) {
  const headersList = {
    Accept: '*/*',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };

  const bodyContent = JSON.stringify({
    first_name,
    last_name,
    role: roles,
  });

  const reqOptions = {
    url: `${backendUrl}/api/users/${id}`,
    method: 'PUT' as const,
    headers: headersList,
    data: bodyContent,
  };

  const { data } = await axios.request(reqOptions);
  return data;
}

// Organization APIs
export async function addOrganization(
  name: string,
  address: string,
  email: string,
  phone: string,
) {
  const headersList = {
    Accept: '*/*',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };

  const bodyContent = JSON.stringify({
    name,
    address,
    email,
    phone,
  });

  const reqOptions = {
    url: `${backendUrl}/api/organizations`,
    method: 'POST' as const,
    headers: headersList,
    data: bodyContent,
  };

  const { data } = await axios.request(reqOptions);
  return data;
}

export async function getOrganizations() {
  const headersList = {
    Accept: '*/*',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };

  const reqOptions = {
    url: `${backendUrl}/api/organizations`,
    method: 'GET' as const,
    headers: headersList,
  };

  const { data } = await axios.request(reqOptions);
  return data;
}

export async function deleteOrganization(id: string) {
  const headersList = {
    Accept: '*/*',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };

  const reqOptions = {
    url: `${backendUrl}/api/organizations/${id}`,
    method: 'DELETE' as const,
    headers: headersList,
  };

  const { data } = await axios.request(reqOptions);
  return data;
}

type UpdateData = {
  userIds: string[];
  orgId: string;
};

export async function addUserToOrganization({ userIds, orgId }: UpdateData) {
  const headersList = {
    Accept: '*/*',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };

  const bodyContent = JSON.stringify({
    user_ids: userIds,
  });

  const reqOptions = {
    url: `${backendUrl}/api/organizations/${orgId}/users`,
    method: 'PUT' as const,
    headers: headersList,
    data: bodyContent,
  };

  const { data } = await axios.request(reqOptions);
  return data;
}

export async function removeUserFromOrganization({ userIds, orgId }: UpdateData) {
  const headersList = {
    Accept: '*/*',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };

  const bodyContent = JSON.stringify({
    user_ids: userIds,
  });

  const reqOptions = {
    url: `${backendUrl}/api/organizations/${orgId}/users`,
    method: 'DELETE' as const,
    headers: headersList,
    data: bodyContent,
  };

  const { data } = await axios.request(reqOptions);
  return data;
}

export async function editOrganization(
  nameOrg: string,
  addressOrg: string,
  phoneOrg: string,
  emailOrg: string,
  id: string,
) {
  const headersList = {
    Accept: '*/*',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };

  const bodyContent = JSON.stringify({
    name: nameOrg,
    address: addressOrg,
    phone: phoneOrg,
    email: emailOrg,
  });

  const reqOptions = {
    url: `${backendUrl}/api/organizations/${id}`,
    method: 'PUT' as const,
    headers: headersList,
    data: bodyContent,
  };

  const { data } = await axios.request(reqOptions);
  return data;
}

// User-Customer Association APIs
export async function getUserDetails(userId: string) {
  const headersList = {
    Accept: '*/*',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };

  const reqOptions = {
    url: `${backendUrl}/api/users/${userId}`,
    method: 'GET' as const,
    headers: headersList,
  };

  const { data } = await axios.request(reqOptions);
  return data;
}

// Password-related APIs
export async function forgotPassword(email: string) {
  const headersList = {
    Accept: '*/*',
    'Content-Type': 'application/json',
  };

  const bodyContent = JSON.stringify({
    email,
  });

  const reqOptions = {
    url: `${backendUrl}/forgot-password`,
    method: 'POST' as const,
    headers: headersList,
    data: bodyContent,
  };

  const { data } = await axios.request(reqOptions);
  return data;
}

export async function resetPassword(token: string, newPassword: string) {
  const headersList = {
    Accept: '*/*',
    'Content-Type': 'application/json',
  };

  const bodyContent = JSON.stringify({
    token,
    new_password: newPassword,
  });

  const reqOptions = {
    url: `${backendUrl}/reset-password`,
    method: 'POST' as const,
    headers: headersList,
    data: bodyContent,
  };

  const { data } = await axios.request(reqOptions);
  return data;
}

export async function changePassword(currentPassword: string, newPassword: string) {
  const headersList = {
    Accept: '*/*',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };

  const bodyContent = JSON.stringify({
    current_password: currentPassword,
    new_password: newPassword,
  });

  const reqOptions = {
    url: `${backendUrl}/change-password`,
    method: 'POST' as const,
    headers: headersList,
    data: bodyContent,
  };

  const { data } = await axios.request(reqOptions);
  return data;
}
